/* UnAnonymous secure vault
 * IndexedDB + WebCrypto (AES-GCM 256) for at-rest encryption of sensitive
 * user artifacts (Mosaic pieces today; voice notes, screenshots, "tomorrow-you"
 * letters next).
 *
 * Philosophy:
 *   - Soft encryption: the key lives on this device only, generated on first run.
 *     This protects against incidental reads (extensions, dev tools casually
 *     scanning storage, accidental exports). It is not a defense against an
 *     attacker with physical device access — that is intentional, because asking
 *     a user in distress to remember a passphrase to reach their own sanctuary
 *     would be cruel.
 *   - The vault never leaves the device. There is no sync, no upload.
 *   - Failures are silent to the user but observable in console; the app must
 *     never crash because the vault hiccupped.
 *
 * A passphrase-protected tier can layer on later without changing this API.
 */

const DB_NAME = "un_vault";
const DB_VERSION = 2;

const STORE_META = "meta";
const STORE_MOSAIC = "mosaic";
const STORE_LETTERS = "letters"; // reserved for tomorrow-you delayed letters
const STORE_DRAFTS_BUFFER = "drafts_buffer"; // in-progress autosave for journal/letters/drafts composers

const META_KEY_ENCKEY = "enc_key_v1";
const META_KEY_MIGRATED_MOSAIC = "migrated_mosaic_v1";

let _dbPromise = null;
let _keyPromise = null;

function isSupported() {
  return (
    typeof indexedDB !== "undefined" &&
    typeof crypto !== "undefined" &&
    !!crypto.subtle
  );
}

function openDb() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META);
      }
      if (!db.objectStoreNames.contains(STORE_MOSAIC)) {
        db.createObjectStore(STORE_MOSAIC, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(STORE_LETTERS)) {
        db.createObjectStore(STORE_LETTERS, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(STORE_DRAFTS_BUFFER)) {
        // Keyed by stable surface name: 'journal' | 'letters' | 'drafts'.
        db.createObjectStore(STORE_DRAFTS_BUFFER);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error("IndexedDB blocked"));
  });
  return _dbPromise;
}

function tx(db, storeName, mode = "readonly") {
  const t = db.transaction(storeName, mode);
  return t.objectStore(storeName);
}

function reqAsPromise(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function getMeta(key) {
  const db = await openDb();
  return reqAsPromise(tx(db, STORE_META).get(key));
}

async function setMeta(key, value) {
  const db = await openDb();
  return reqAsPromise(tx(db, STORE_META, "readwrite").put(value, key));
}

async function ensureKey() {
  if (_keyPromise) return _keyPromise;
  _keyPromise = (async () => {
    const existing = await getMeta(META_KEY_ENCKEY);
    if (existing) return existing;
    // First-run: generate a fresh AES-GCM 256 key.
    const key = await crypto.subtle.generateKey(
      { name: "AES-GCM", length: 256 },
      false, // non-extractable
      ["encrypt", "decrypt"],
    );
    await setMeta(META_KEY_ENCKEY, key);
    return key;
  })().catch((e) => {
    _keyPromise = null;
    throw e;
  });
  return _keyPromise;
}

function bufToB64(buf) {
  const bytes = new Uint8Array(buf);
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}

function b64ToBuf(b64) {
  const s = atob(b64);
  const bytes = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) bytes[i] = s.charCodeAt(i);
  return bytes.buffer;
}

async function encryptValue(value) {
  const key = await ensureKey();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plaintext = new TextEncoder().encode(JSON.stringify(value));
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, plaintext);
  return { iv: bufToB64(iv), ct: bufToB64(ct) };
}

async function decryptRecord(record) {
  if (!record || !record.iv || !record.ct) return null;
  try {
    const key = await ensureKey();
    const iv = new Uint8Array(b64ToBuf(record.iv));
    const pt = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv },
      key,
      b64ToBuf(record.ct),
    );
    return JSON.parse(new TextDecoder().decode(pt));
  } catch (_e) {
    return null;
  }
}

// --- Generic vault API (per object store) ---

async function vaultPut(storeName, id, value) {
  if (!isSupported()) throw new Error("Vault not supported on this device");
  const db = await openDb();
  const enc = await encryptValue(value);
  const record = {
    id,
    iv: enc.iv,
    ct: enc.ct,
    createdAt: value.createdAt || new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  await reqAsPromise(tx(db, storeName, "readwrite").put(record));
  return id;
}

async function vaultGet(storeName, id) {
  if (!isSupported()) return null;
  const db = await openDb();
  const record = await reqAsPromise(tx(db, storeName).get(id));
  return decryptRecord(record);
}

async function vaultList(storeName) {
  if (!isSupported()) return [];
  const db = await openDb();
  const records = await reqAsPromise(tx(db, storeName).getAll());
  const out = [];
  for (const r of records) {
    // Decrypt sequentially to avoid spiking the crypto worker for huge lists.
    // For our scale (tens to low hundreds of items) this is comfortably fast.
    // eslint-disable-next-line no-await-in-loop
    const v = await decryptRecord(r);
    if (v) out.push(v);
  }
  return out;
}

async function vaultDelete(storeName, id) {
  if (!isSupported()) return;
  const db = await openDb();
  await reqAsPromise(tx(db, storeName, "readwrite").delete(id));
}

async function vaultClear(storeName) {
  if (!isSupported()) return;
  const db = await openDb();
  await reqAsPromise(tx(db, storeName, "readwrite").clear());
}

export {
  isSupported as isVaultSupported,
  vaultPut,
  vaultGet,
  vaultList,
  vaultDelete,
  vaultClear,
  getMeta,
  setMeta,
  STORE_MOSAIC,
  STORE_LETTERS,
  STORE_DRAFTS_BUFFER,
  META_KEY_MIGRATED_MOSAIC,
};

/* ---------------- Buffered draft (in-progress composer text) ---------------- */
/*
 * Lightweight per-surface buffer for in-progress composer text. Used by the
 * AutoSaveIndicator-paired hooks in Journal, Letters, and Drafts. Stored
 * encrypted in the same vault as the rest of the user's sensitive content.
 *
 * `key` should be a stable surface name like "journal" | "letters" | "drafts".
 */
export async function setDraftBuffer(key, payload, { strict = false } = {}) {
  if (!isSupported()) {
    if (strict) throw new Error("Encrypted draft storage is unavailable.");
    return;
  }
  try {
    const db = await openDb();
    const enc = await encryptValue({ payload, updatedAt: new Date().toISOString() });
    await reqAsPromise(
      tx(db, STORE_DRAFTS_BUFFER, "readwrite").put(enc, key),
    );
  } catch (e) {
    // Background autosave remains best-effort; backup restore must report failure.
    if (strict) throw e;
    console.warn("[vault] setDraftBuffer failed:", e?.message || e);
  }
}

export async function getDraftBuffer(key, { strict = false } = {}) {
  if (!isSupported()) {
    if (strict) throw new Error("Encrypted draft storage is unavailable.");
    return null;
  }
  try {
    const db = await openDb();
    const enc = await reqAsPromise(tx(db, STORE_DRAFTS_BUFFER).get(key));
    if (!enc) return null;
    const decoded = await decryptRecord(enc);
    if (!decoded && strict) throw new Error("Your draft could not be decrypted.");
    return decoded ? decoded.payload : null;
  } catch (e) {
    if (strict) throw e;
    console.warn("[vault] getDraftBuffer failed:", e?.message || e);
    return null;
  }
}

export async function clearDraftBuffer(key) {
  if (!isSupported()) return;
  try {
    const db = await openDb();
    await reqAsPromise(tx(db, STORE_DRAFTS_BUFFER, "readwrite").delete(key));
  } catch (e) {
    console.warn("[vault] clearDraftBuffer failed:", e?.message || e);
  }
}
